import { cn } from "@/lib/cn";
import { Avatar } from "@/components/Avatar";
import type { DashboardMode } from "@/lib/dashboard-mode";
import { getRoleLabel, papeisAtribuiveis } from "./role-meta";
import type { AtendimentoEmCurso } from "@/lib/em-atendimento";
import { SeloEmAtendimento } from "@/components/shared/SeloEmAtendimento";
import { TeamMemberActions } from "./TeamMemberActions";
import { useAuth } from "@/lib/auth";

type Props = {
  member: any;
  mode: DashboardMode;
  /** Atendimento em curso desse membro, se houver — selo "Em atendimento". */
  atendimento?: AtendimentoEmCurso;
  canManage: boolean;
  viewerIsOwner: boolean;
  customRoles: any[];
  rolePending: boolean;
  suspendPending: boolean;
  reactivatePending: boolean;
  removePending: boolean;
  onOpenProfile: (membershipId: string) => void;
  onRoleChange: (membershipId: string, value: string) => void;
  onSuspend: (member: any) => void;
  onReactivate: (member: any) => void;
  onRemove: (member: any) => void;
};

/**
 * O membro no celular. A tabela de seis colunas exigia 433px de rolagem lateral pra chegar nas
 * ações, e os papéis chegavam cortados no meio da palavra — "PROP", "Dent", "Assis".
 *
 * Aqui cabe tudo sem rolar: nome e e-mail em cima, papel e situação embaixo por extenso, e as ações
 * no mesmo menu do desktop. Carga atual e responsabilidades ficam de fora de propósito — são leitura
 * de gestão, e quem precisa delas abre o perfil.
 */
export function TeamMemberCard({
  member: m,
  mode,
  atendimento,
  canManage,
  viewerIsOwner,
  customRoles,
  rolePending,
  suspendPending,
  reactivatePending,
  removePending,
  onOpenProfile,
  onRoleChange,
  onSuspend,
  onReactivate,
  onRemove,
}: Props) {
  const isOwner = m.role === "OWNER";
  const souProprietario = useAuth().user?.activeOrganization?.role === "OWNER";
  const isSuspended = m.status === "SUSPENDED";
  const roleLabel = m.customRole?.label ?? getRoleLabel(m.role, mode);
  const podeTrocarPapel = canManage && !isOwner;

  return (
    <li className="px-1 py-3">
      {/* Linha 1: identidade com a largura toda. O seletor de papel foi pra linha de baixo porque,
          na mesma linha, ele espremia o nome a ponto de truncar — "Rodrigo Peixoto" não cabia. */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => onOpenProfile(m.id)}
          className="flex min-h-11 min-w-0 flex-1 items-start gap-3 text-left"
        >
          <Avatar src={m.user.image} name={m.user.name} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-zinc-900 dark:text-foreground">
              {m.user.name || "(sem nome)"}
            </span>
            <span className="block truncate text-xs text-zinc-500 dark:text-muted-foreground">
              {m.user.email}
            </span>
          </span>
        </button>
        <TeamMemberActions
          member={m}
          canManage={canManage}
          suspendPending={suspendPending}
          reactivatePending={reactivatePending}
          removePending={removePending}
          onOpenProfile={onOpenProfile}
          onSuspend={onSuspend}
          onReactivate={onReactivate}
          onRemove={onRemove}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 pl-11">
        {atendimento && <SeloEmAtendimento atendimento={atendimento} />}
        {!podeTrocarPapel && (
          <span
            className={cn(
              "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
              isOwner
                ? "bg-brand-900 text-white"
                : "bg-zinc-100 text-zinc-700 dark:bg-muted dark:text-foreground/80",
            )}
          >
            {roleLabel}
          </span>
        )}
        <span
          className={cn(
            "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
            isSuspended
              ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
              : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
          )}
        >
          {isSuspended ? "Suspenso" : "Ativo"}
        </span>
        {podeTrocarPapel && (
          <select
            value={
              viewerIsOwner
                ? m.customRoleId
                  ? `custom:${m.customRoleId}`
                  : `role:${m.role}`
                : m.role
            }
            onChange={(e) =>
              onRoleChange(m.id, viewerIsOwner ? e.target.value : `role:${e.target.value}`)
            }
            disabled={rolePending}
            aria-label="Papel do membro"
            className="h-11 min-w-0 max-w-full rounded-md border border-zinc-200 bg-white px-2 text-base font-medium text-zinc-700 dark:border-border dark:bg-background dark:text-foreground"
          >
            {viewerIsOwner ? (
              <>
                <optgroup label="Papéis padrão">
                  {papeisAtribuiveis(mode, m.role, souProprietario).map((r) => (
                    <option key={r} value={`role:${r}`}>
                      {getRoleLabel(r, mode)}
                    </option>
                  ))}
                </optgroup>
                {customRoles.length > 0 && (
                  <optgroup label="Papéis customizados">
                    {customRoles.map((cr: any) => (
                      <option key={cr.id} value={`custom:${cr.id}`}>
                        {cr.label}
                      </option>
                    ))}
                  </optgroup>
                )}
              </>
            ) : (
              papeisAtribuiveis(mode, m.role, souProprietario).map((r) => (
                <option key={r} value={r}>
                  {getRoleLabel(r, mode)}
                </option>
              ))
            )}
          </select>
        )}
      </div>
    </li>
  );
}
